import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  approveConfigDraft,
  publishConfigDraft,
  rebaseSoundConfigDraft,
} from "../runtime-config/client";
import {
  RuntimeConfigRequestError,
  type ConfigDraft,
} from "../runtime-config/contracts";
import { soundQueryKey } from "./api";
import type { PendingSoundDraft } from "./contracts";

type DraftActionState = {
  draftId: string;
  status:
    | "approving"
    | "publishing"
    | "published"
    | "rebasing"
    | "rebased"
    | "error";
  message?: string;
} | null;

export function ProductionSoundDrafts({
  activeReleaseId,
  drafts,
}: {
  activeReleaseId: string | null;
  drafts: PendingSoundDraft[];
}) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<DraftActionState>(null);

  if (drafts.length === 0) return null;

  const approveAndPublish = async (pending: PendingSoundDraft) => {
    try {
      let draft = toConfigDraft(pending);
      if (draft.status === "validated") {
        setAction({ draftId: draft.id, status: "approving" });
        draft = await approveConfigDraft(draft);
      }
      setAction({ draftId: draft.id, status: "publishing" });
      await publishConfigDraft(draft);
      setAction({
        draftId: draft.id,
        status: "published",
        message:
          "The change is published. Voices show as Live once the system confirms the update, usually within a minute.",
      });
      await queryClient.invalidateQueries({ queryKey: soundQueryKey });
    } catch (error) {
      setAction({
        draftId: pending.id,
        status: "error",
        message: draftErrorMessage(error),
      });
    }
  };

  const rebaseAndReview = async (pending: PendingSoundDraft) => {
    try {
      setAction({ draftId: pending.id, status: "rebasing" });
      const result = await rebaseSoundConfigDraft(toConfigDraft(pending));
      setAction({
        draftId: pending.id,
        status: "rebased",
        message:
          result.status === "already_applied"
            ? "This change is already present in the current release. The stale draft was retired."
            : "The change was safely rebased onto the current release. Review the refreshed diff, then approve and publish it.",
      });
      await queryClient.invalidateQueries({ queryKey: soundQueryKey });
    } catch (error) {
      setAction({
        draftId: pending.id,
        status: "error",
        message: draftErrorMessage(error),
      });
    }
  };

  return (
    <section className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-start gap-2">
        <ShieldCheck className="mt-0.5 size-5 text-destructive" />
        <div>
          <h2 className="font-semibold">Voice changes waiting for approval</h2>
          <p className="text-sm text-muted-foreground">
            These changes affect what customers hear. An authorized
            administrator must review and approve them before publication.
          </p>
        </div>
      </div>

      {drafts.map((draft) => {
        const isStale =
          activeReleaseId !== null && draft.base_release_id !== activeReleaseId;
        const busy =
          action?.draftId === draft.id &&
          ["approving", "publishing", "rebasing"].includes(action.status);
        const canPublish =
          !isStale && ["validated", "approved"].includes(draft.status);
        return (
          <article
            key={draft.id}
            id={`sound-draft-${draft.id}`}
            className="scroll-mt-4 space-y-3 rounded-md border bg-background p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm">{draft.id}</span>
                  <Badge variant="outline">{draft.status}</Badge>
                  {draft.ticket_reference ? (
                    <Badge variant="secondary">{draft.ticket_reference}</Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-sm">{draft.reason}</p>
                <p
                  className="mt-1 text-xs text-muted-foreground"
                  title={draft.created_by_hash ?? undefined}
                >
                  Requested
                  {draft.created_at
                    ? ` ${new Date(draft.created_at).toLocaleString()}`
                    : " (date unavailable)"}
                </p>
              </div>
              <Button
                type="button"
                variant={isStale ? "outline" : "destructive"}
                disabled={isStale ? busy : !canPublish || busy}
                onClick={() =>
                  isStale ? rebaseAndReview(draft) : approveAndPublish(draft)
                }
              >
                {busy
                  ? action?.status === "rebasing"
                    ? "Rebasing…"
                    : action?.status === "approving"
                      ? "Approving…"
                      : "Publishing…"
                  : isStale
                    ? "Rebase and review"
                    : draft.status === "approved"
                      ? "Publish signed release"
                      : "Approve and publish"}
              </Button>
            </div>

            <VoiceDiff draft={draft} />

            {action?.draftId === draft.id && action.message ? (
              <Alert
                variant={action.status === "error" ? "destructive" : "default"}
              >
                {["published", "rebased"].includes(action.status) ? (
                  <CheckCircle2 />
                ) : null}
                <AlertTitle>
                  {action.status === "error"
                    ? "Draft action failed"
                    : action.status === "rebased"
                      ? "Draft rebased"
                      : "Published"}
                </AlertTitle>
                <AlertDescription>{action.message}</AlertDescription>
              </Alert>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}

function VoiceDiff({ draft }: { draft: PendingSoundDraft }) {
  const groups = [
    ["Add", draft.voice_diff.added_voice_ids],
    ["Turn off", draft.voice_diff.disabled_voice_ids ?? []],
    ["Change", draft.voice_diff.changed_voice_ids],
    ["Remove", draft.voice_diff.removed_voice_ids],
  ] as const;
  return (
    <div className="grid gap-2 text-sm sm:grid-cols-4">
      {groups.map(([label, voiceIds]) => (
        <div key={label} className="rounded border p-2">
          <div className="font-medium">
            {label} · {voiceIds.length}
          </div>
          <div className="mt-1 break-all font-mono text-xs text-muted-foreground">
            {voiceIds.length ? voiceIds.join(", ") : "None"}
          </div>
        </div>
      ))}
    </div>
  );
}

function toConfigDraft(draft: PendingSoundDraft): ConfigDraft {
  return {
    id: draft.id,
    version: draft.version,
    status: draft.status as ConfigDraft["status"],
    created_by: draft.created_by_hash ?? "redacted",
    reason: draft.reason,
    ticket_reference: draft.ticket_reference,
  };
}

function draftErrorMessage(error: unknown) {
  if (error instanceof TypeError) {
    return "The approval service could not be reached. Refresh Sound and retry. If it still fails, ask an administrator to check the CRM API connection.";
  }
  if (error instanceof RuntimeConfigRequestError) {
    if (error.code === "forbidden") {
      return "Approval was refused. Your account does not have permission to approve and publish production configuration.";
    }
    if (error.code === "conflict") {
      if (error.message === "sound_rebase_conflict") {
        return "This voice also changed in the newer release, so the draft could not be safely rebased. Refresh Sound and review the current voice before creating a replacement change.";
      }
      if (error.message === "sound_draft_scope_invalid") {
        return "This stale draft contains changes outside Sound and cannot be safely rebased here. Ask an administrator to review it in the runtime configuration control plane.";
      }
      return "The draft or active release changed. Refresh Sound and review the current immutable diff before retrying.";
    }
    if (error.code === "invalid") {
      return "The signed control plane rejected this draft or its current ElevenLabs validation/audition evidence.";
    }
    return error.message;
  }
  return error instanceof Error
    ? error.message
    : "Unknown draft action failure.";
}
